import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { login } from "../utils/api";
import { useAuth, UserType } from "../context/AuthContext";
import * as Sentry from "@sentry/react";

const Login: React.FC = () => {
  //const [username, setUsername] = useState<string>('gerardo.najera@grupociisa.com')
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login: authenticateUser } = useAuth();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [processingSSO, setProcessingSSO] = useState<boolean>(false); // ✅ Estado para evitar procesamiento múltiple
  const [isTraditionalLogin, setIsTraditionalLogin] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false); // ✅ Detectar si el usuario empezó a escribir

  // ✅ Detectar tipo de dispositivo
  const isHandHeldDevice = (): boolean => {
    const ua = navigator.userAgent.toLowerCase();
    return (
      ua.includes("zebra") ||
      ua.includes("tc26") ||
      ua.includes("tc21") ||
      ua.includes("android")
    );
  };

  const deviceType = isHandHeldDevice() ? "handheld" : "windows";

  console.log("Tipo de dispositivo detectado:", deviceType);

  const userAgent = navigator.userAgent.toLowerCase();

  //console.log('Rosa María Rodríguez Rodríguez' ,('Rosa María Rodríguez Rodríguez').length);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (e.target.value) setIsFormDirty(true); // ✅ Marcar que el usuario empezó a escribir
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (e.target.value) setIsFormDirty(true); // ✅ Marcar que el usuario empezó a escribir
  };

  const handleBlur = () => {
    if (!username.includes("@")) {
      setUsername(`${username}@grupociisa.com`);
    }
  };
  // ✅ Procesar callback de SSO cuando el componente se monta o cambia la URL
  useEffect(() => {
    if (processingSSO) return;

    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const userParam = params.get("user");
    const errorParam = params.get("error");

    // Si hay error en la URL
    if (errorParam) {
      setError(`Error de autenticación: ${errorParam}`);
      // Limpiar la URL reemplazando la historia
      window.history.replaceState({}, "", "/login");
      return;
    }
    console.log(`usuario desde parámetro: ${userParam}`);

    // Si es callback de SSO (viene de /auth/callback)
    if (token && userParam) {
      setProcessingSSO(true);
      setLoading(true);
      try {
        const userData = JSON.parse(decodeURIComponent(userParam));
        console.log("Datos del usuario desde SSO:", userData);
        const authenticatedUser: UserType = {
          User: {
            name: userData.name,
            position: userData.position,
            email: userData.email,
            role: userData.role,
            is_active: userData.is_active,
          },
          token: token,
          permissions: userData.permissions || [],
        };

        // ✅ Limpiar la URL ANTES de navegar
        window.history.replaceState({}, "", "/login");

        authenticateUser(authenticatedUser);
        Sentry.setUser({
          email: authenticatedUser.User.email,
          name: authenticatedUser.User.name,
          role: authenticatedUser.User.role,
        });

        console.log(
          `usuario desde parámetro: ${userParam} role: ${userData.role}`,
        );
        setTimeout(() => {
          // Redirigir según el rol
          if (userData.role === "Pistero") {
            navigate("/captura-combustible");
          } else if (userData.role === "Administrativos") {
            navigate("/panel-supervisor-visitas");
          } else {
            navigate("/dashboard");
          }
        }, 100);
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error procesando callback SSO:", error);
        setError("Error al procesar la autenticación con Microsoft");
        window.history.replaceState({}, "", "/login");
        setProcessingSSO(false);
        setLoading(false);
      }
    }
  }, [location, navigate, authenticateUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsTraditionalLogin(true);
    setLoading(true);

    try {
      //valida el usuario contra el API que valida contra el AD
      const { data } = await login({ username, password });

      if (!data.user.is_active) {
        setError(
          "Su cuenta ha sido desactivada. Por favor, contacte al administrador.",
        );
        setIsTraditionalLogin(false);
        setLoading(false);
        return;
      }

      const user: UserType = {
        User: {
          name: data.user.name,
          position: data.user.position,
          email: data.user.email,
          role: data.user.role,
          is_active: data.user.is_active,
        },
        //role: 'admin',
        token: data.token,
        permissions: data.user.permissions || [], // Asignar permisos del usuario
      };

      authenticateUser(user);
      Sentry.setUser({
        email: user.User.email,
        name: user.User.name,
        role: user.User.role,
      });

      // const permissions = await getPermissionByUserNameOrRole(data.user.email, data.user.role);
      // console.log('Permisos obtenidos: ', permissions);

      // user.permissions = permissions || [];
      // authenticateUser(user);
      //localStorage.setItem('token', data.token);
      console.log("Role del usuario en login: ", user.User.role);
      if (user.User.role === "Pistero") {
        navigate("/captura-combustible");
      }
      if (user.User.role === "Administrativos") {
        navigate("/panel-supervisor-visitas");
      } else {
        navigate("/dashboard");
      }
    } catch (error: any) {
      // ${error.response?.data?.message || ''}
      Sentry.captureException(error);
      setError(`Credenciales incorrectas. Inténtalo de nuevo. `);
    } finally {
      setLoading(false);
      setIsTraditionalLogin(false);
    }
  };

  const DeviceIndicator = () => {
    const [deviceInfo, setDeviceInfo] = useState({
      type: "",
      icon: "",
      color: "",
    });

    React.useEffect(() => {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes("zebra") || ua.includes("tc26") || ua.includes("tc21")) {
        setDeviceInfo({
          type: "HH ZEBRA",
          icon: "fa-mobile-alt",
          color: "warning",
        });
      } else if (ua.includes("windows")) {
        setDeviceInfo({
          type: "WINDOWS",
          icon: "fa-windows",
          color: "info",
        });
      } else {
        setDeviceInfo({
          type: navigator.platform,
          icon: "fa-question-circle",
          color: "secondary",
        });
      }
    }, []);

    return (
      <div
        className={`position-fixed top-0 end-0 m-2 badge bg-${deviceInfo.color} p-2 shadow-sm`}
        style={{ zIndex: 1050, opacity: 0.8, fontSize: "0.75rem" }}
        title={`Dispositivo: ${deviceInfo.type}`}
      >
        <i className={`fas ${deviceInfo.icon} me-1`}></i>
        {deviceInfo.type}
      </div>
    );
  };

  const handleSSOLogin = async () => {
    // ✅ Prevenir SSO si:
    // 1. Es dispositivo Hand Held
    // 2. Hay login tradicional en curso
    // 3. El usuario empezó a escribir en el formulario
    // 4. Hay loading en curso
    if (isHandHeldDevice()) {
      setError(
        "El inicio de sesión con Microsoft no está disponible en este dispositivo",
      );
      return;
    }

    if (isTraditionalLogin || loading || isFormDirty) {
      console.log(
        "No se puede iniciar SSO: tradicional en curso o formulario sucio",
      );
      if (isFormDirty) {
        setError(
          "Por favor, complete el inicio de sesión tradicional o limpie el formulario para usar Microsoft",
        );
      }
      return;
    }

    setLoading(true);
    try {
      // Obtener la URL de autorización de Azure desde el backend
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/auth/azure/login`,
      );

      //const response = await loginSSO();

      const { authUrl } = await response.json();

      // Redirigir a Azure para autenticación
      window.location.href = authUrl;
    } catch (error) {
      setError("Error al iniciar sesión con Microsoft");
      setLoading(false);
    }
  };

  // ✅ Función para limpiar el formulario (útil si el usuario cambió de opinión)
  const clearForm = () => {
    setUsername("");
    setPassword("");
    setIsFormDirty(false);
    setError("");
  };

  // NUEVO: Componente para el botón de SSO
  const SSOButton = () => {
    // No mostrar en Hand Held
    if (isHandHeldDevice()) {
      return null;
    }
    const isDisabled =
      loading || isTraditionalLogin || isFormDirty || processingSSO;

    let buttonTitle = "Iniciar sesión con Microsoft";
    if (isFormDirty) {
      buttonTitle = "Limpia el formulario para usar inicio con Microsoft";
    } else if (isTraditionalLogin) {
      buttonTitle = "Espera a que termine el inicio de sesión tradicional";
    }

    return (
      <>
        <div className="text-center my-3">
          <span className="text-muted">o</span>
        </div>
        <button
          onClick={handleSSOLogin}
          disabled={isDisabled}
          className="btn btn-outline-primary w-100 mt-2"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            opacity: isDisabled ? 0.6 : 1,
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
          title={buttonTitle}
        >
          <i className="fab fa-microsoft"></i>
          {loading && !isTraditionalLogin
            ? "Conectando..."
            : "Iniciar sesión con Microsoft"}
        </button>
        {isFormDirty && (
          <button
            onClick={clearForm}
            className="btn btn-link btn-sm w-100 mt-2"
            style={{ fontSize: "0.8rem" }}
          >
            Limpiar formulario para usar Microsoft
          </button>
        )}
      </>
    );
  };

  return (
    <div className="container d-flex flex-column align-items-center justify-content-center vh-100">
      <DeviceIndicator />
      <div
        className="card p-4 shadow"
        style={{ maxWidth: "500px", width: "100%" }}
      >
        <img
          src={require("../assets/CiisaLogo.png")}
          alt="logo"
          className="img-fluid mb-3"
          style={{ height: "50px", width: "100%", objectFit: "contain" }}
        />
        <h1 className="mb-4 text-center">Iniciar Sesión</h1>
        {/* <small className='text-center'>Dispositvo: {userAgent.toLowerCase().includes('windows') ? 'Windows' : userAgent.toLowerCase().includes('zebra') ? 'Hand Held' : ''}</small> */}
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show text-center"
            role="alert"
          >
            {error}
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setError("")}
            ></button>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <input
              style={{
                width: "100%",
                borderRadius: "5px",
                border: "1px solid #ccc",
                padding: "10px",
              }}
              type={"text"}
              placeholder="Ingrese su Usuario"
              value={username}
              onChange={handleUsernameChange}
              disabled={loading || processingSSO}
              onBlur={handleBlur}
              required
            />
          </div>
          <div className="mb-3 position-relative">
            <input
              ref={inputRef}
              style={{
                width: "100%",
                borderRadius: "5px",
                border: "1px solid #ccc",
                padding: "10px",
                paddingRight: "40px",
              }}
              type={showPassword ? "text" : "password"}
              placeholder="Contraseña"
              value={password}
              onChange={handlePasswordChange}
              disabled={loading || processingSSO}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="btn btn-light position-absolute"
              style={{
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                padding: "5px",
              }}
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading || processingSSO}
            >
              <i
                className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
              />
            </button>
          </div>
          <button
            className="btn btn-primary w-100"
            type="submit"
            disabled={loading || processingSSO}
          >
            {loading && isTraditionalLogin
              ? "Iniciando sesión..."
              : "Iniciar Sesión"}
          </button>
        </form>

        {/* <SSOButton /> */}
        <div className="text-center my-3">
          <span className="text-muted">V2.0</span>
        </div>
      </div>
    </div>
  );
};

export default Login;
